import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import KeyboardComponent from '../../../Elements/keyboard'
import keyboardImg from '../../../../img/svg/input/keyboard.svg'
import RoomChargeKeypad from '../../RoomChargeKeypad'
import { SearchMode } from '../../types'
import { RoomChargeActionButton, RoomChargeActionIcon } from '../index.styled'
import {
	SearchFieldBox, SearchFieldDot, SearchFieldError, SearchFieldGroup, SearchFieldInput, SearchFieldKeyboardButton, SearchFieldLabel, SearchFieldValue, SearchFieldValueRow, SearchTab, SearchTabIcon, SearchTabs,
} from './index.styled'

const Keyboard = KeyboardComponent as unknown as React.ComponentType<{
	currentValue: string
	handleType: (value: string) => void
	onlyNumbers?: boolean
	close: () => void
	enter: () => void
}>

const ROOM_PLACEHOLDER_DOTS = [0, 1, 2]

type Props = {
	searchMode: SearchMode
	canSearchGuest: boolean
	roomNumber: string
	guestName: string
	maxNameLength: number
	errorMessage: string | null
	isLoading: boolean
	onSearchModeChange: (mode: SearchMode) => void
	onQueryChange: (value: string) => void
	onSubmit: () => void
}

const RoomChargeSearchStep = React.memo(({
	searchMode, canSearchGuest, roomNumber, guestName, maxNameLength, errorMessage, isLoading,
	onSearchModeChange, onQueryChange, onSubmit,
}: Props) => {
	const { t } = useTranslation()
	const [keyboardIsOpen, setKeyboardIsOpen] = useState(false)
	const inputRef = useRef<HTMLInputElement>(null)
	const keyboardHost = document.getElementById('content')

	const isRoomMode = searchMode === 'room'
	const query = isRoomMode ? roomNumber : guestName
	const isSubmitDisabled = !query.trim() || isLoading

	const tabs = useMemo(() => [
		{ id: 'room' as const, label: t('RoomCharge.search.byRoom'), icon: 'hash' as const },
		{ id: 'name' as const, label: t('RoomCharge.search.byName'), icon: 'search' as const },
	], [t])

	const closeKeyboard = useCallback(() => setKeyboardIsOpen(false), [])
	const toggleKeyboard = useCallback(() => setKeyboardIsOpen(current => !current), [])

	useEffect(() => {
		if (!isRoomMode) inputRef.current?.focus()
	}, [isRoomMode])

	const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		onQueryChange(event.target.value)
	}, [onQueryChange])

	const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter' && !isSubmitDisabled) onSubmit()
	}, [isSubmitDisabled, onSubmit])

	const handleKeyboardEnter = useCallback(() => {
		closeKeyboard()
		if (!isSubmitDisabled) onSubmit()
	}, [closeKeyboard, isSubmitDisabled, onSubmit])

	const handleKeypadInput = useCallback((character: string) =>
		onQueryChange(roomNumber + character), [onQueryChange, roomNumber])

	const handleKeypadDelete = useCallback(() =>
		onQueryChange(roomNumber.slice(0, -1)), [onQueryChange, roomNumber])

	const handleTabClick = useCallback((mode: SearchMode) => () => {
		closeKeyboard()
		onSearchModeChange(mode)
	}, [closeKeyboard, onSearchModeChange])

	const submitIcon = canSearchGuest ? 'user' : 'check-circle'
	const submitLabel = canSearchGuest ? t('RoomCharge.search.submit') : t('RoomCharge.search.confirmRoom')

	return (
		<>
			{canSearchGuest && <SearchTabs>
				{tabs.map(tab => (
					<SearchTab key={tab.id}
										 type="button"
										 className={searchMode === tab.id ? 'active' : ''}
										 onClick={handleTabClick(tab.id)}>
						<SearchTabIcon icon={tab.icon} />
						{tab.label}
					</SearchTab>
				))}
			</SearchTabs>}

			<SearchFieldGroup>
				<SearchFieldLabel>
					{isRoomMode ? t('RoomCharge.search.roomLabel') : t('RoomCharge.search.nameLabel')}
				</SearchFieldLabel>
				<SearchFieldBox className={errorMessage ? 'error' : ''}>
					{errorMessage && <SearchFieldError>{errorMessage}</SearchFieldError>}
					<SearchFieldValueRow>
						{isRoomMode ?
							roomNumber ?
								<SearchFieldValue className="spaced">{roomNumber.split('').join(' ')}</SearchFieldValue> :
								ROOM_PLACEHOLDER_DOTS.map(dot => <SearchFieldDot key={dot} />) :
								<>
									<SearchFieldInput ref={inputRef}
																	value={guestName}
																	onChange={handleChange}
																	onKeyDown={handleKeyDown}
																	maxLength={maxNameLength}
																	autoComplete="off"
																	placeholder={t('RoomCharge.search.namePlaceholder')}
																	aria-label={t('RoomCharge.search.nameLabel')} />
									<SearchFieldKeyboardButton type="button"
																					 onClick={toggleKeyboard}
																					 aria-label={t('RoomCharge.search.openKeyboard')}>
										<img src={keyboardImg} alt="keyboard" />
									</SearchFieldKeyboardButton>
								</>}
					</SearchFieldValueRow>
				</SearchFieldBox>
			</SearchFieldGroup>

			{isRoomMode &&
				<RoomChargeKeypad onInput={handleKeypadInput}
													onDelete={handleKeypadDelete}
													deleteLabel={t('RoomCharge.keypad.delete')} />}

			{keyboardIsOpen && !isRoomMode && keyboardHost && ReactDOM.createPortal(
				<Keyboard currentValue={guestName}
									handleType={onQueryChange}
									close={closeKeyboard}
									enter={handleKeyboardEnter} />, keyboardHost)}

			<RoomChargeActionButton type="button" disabled={isSubmitDisabled} onClick={onSubmit}>
				<RoomChargeActionIcon icon={isLoading ? 'loader' : submitIcon} className={isLoading ? 'spinning' : ''} />
				{submitLabel}
			</RoomChargeActionButton>
		</>
	)
})

RoomChargeSearchStep.displayName = 'RoomChargeSearchStep'
export default RoomChargeSearchStep
